import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Soins from "@/pages/Soins";
import SoinDetail from "@/pages/SoinDetail";
import Formations from "@/pages/Formations";
import Sejour from "@/pages/Sejour";
import Contact from "@/pages/Contact";
import Ateliers from "@/pages/Ateliers";
import Constellations from "@/pages/Constellations";
import Admin from "@/pages/Admin";
import LandingTrek from "@/pages/LandingTrek";
import LandingCNT from "@/pages/LandingCNT";
import LandingReflexologie from "@/pages/LandingReflexologie";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Guide from "@/pages/Guide";
import GuideBouffees from "@/pages/GuideBouffees";
import Guides from "@/pages/Guides";
import { SOINS } from "@/data/content";

const SEO_MAP = {
  "/": ["Julie Zittel — Thérapeute, Magnétiseuse & Rebouteuse | Bayonne · Bellignat", "Julie Zittel, thérapeute magnétiseuse rebouteuse à Bayonne et Bellignat : médecine chinoise, sophrologie, ostéothérapie, ventouses et massages du monde. Soins énergétiques, formations et trek initiatique au Sahara."],
  "/soins": ["Les Soins — Julie Zittel | Ventouses, Magnétisme, Chi Nei Tsang", "Treize soins pour rééquilibrer corps et esprit : Chi Nei Tsang, ventouses, magnétisme, LaHoChi, réflexologie plantaire, ostéothérapie, shiatsu, massages. Bayonne & Bellignat."],
  "/formations": ["Formations — Julie Zittel | Massages, Magnétisme, LaHoChi", "Formations certifiantes et qualifiantes en massothérapie et énergétique : Chi Nei Tsang, réflexologie plantaire, LaHoChi, drainage lymphatique, massage crânien. Groupes restreints."],
  "/sejour-initiatique": ["Séjour Initiatique — Trek Sahara 7-14 déc. 2026 | Julie Zittel", "Trek initiatique dans le désert du Sahara au Maroc, du 7 au 14 décembre 2026 : marche, silence, breathwork et nuits sous les étoiles. Réservez votre place."],
  "/constellations": ["Constellations familiales, systémiques & karmiques — Julie Zittel", "Constellations familiales, systémiques, karmiques et individuelles, en groupe ou en séance solo, à Bayonne et Bellignat : révéler ce qui se joue, apaiser, se libérer."],
  "/ateliers": ["Ateliers — Breathwork, Kintsugi, Massage des mains | Julie Zittel", "Ateliers en petit groupe à Bayonne et Bellignat : breathwork, kintsugi « Vibre ta voix », massage des mains Shinzu. Dates et tarifs sur demande."],
  "/trek-sahara": ["Trek initiatique Sahara 7-14 déc. 2026 — 960 € tout compris | Julie Zittel", "8 jours de trek initiatique dans le désert du Sahara au Maroc : marche avec les nomades, tentes berbères, breathwork, constellations sous les étoiles. 960 € tout compris, plus que 3 places."],
  "/formation-chi-nei-tsang": ["Formation Chi Nei Tsang | Formation professionnelle – Julie Zittel", "Formation professionnelle en Chi Nei Tsang avec Julie Zittel. 5 jours de formation en présentiel pour approfondir le toucher, l'écoute corporelle et l'accompagnement du ventre."],
  "/formation-reflexologie-plantaire": ["Formation Réflexologie plantaire à Bayonne (Pays Basque) — Julie Zittel", "Apprendre la réflexologie plantaire avec Julie Zittel, thérapeute et formatrice depuis plus de 25 ans. Formation de 5 jours à Bayonne, sans prérequis, très orientée pratique : zones réflexes, toucher, déroulement d'une séance."],
  "/contact": ["Contact — Julie Zittel | Bayonne & Bellignat", "Contactez Julie Zittel pour un soin, une formation ou le trek initiatique au Sahara. Cabinets à Bayonne (10 rue Gosse) et Bellignat (14 rue Castellion Prolongée)."],
  "/blog": ["Le Journal — Julie Zittel | Réflexologie, massages & bien-être à Bayonne", "Articles simples et incarnés sur la réflexologie plantaire, les massages et les approches du corps, par Julie Zittel, thérapeute et formatrice à Bayonne."],
  "/guide": ["Guide offert : 3 gestes de réflexologie plantaire à faire chez soi — Julie Zittel", "Recevez gratuitement le guide « 3 gestes de réflexologie plantaire à faire chez soi » par Julie Zittel, thérapeute à Bayonne. Un rituel bien-être simple et doux pour vos pieds."],
  "/guides": ["Guides offerts — Réflexologie & bien-être | Julie Zittel, Bayonne", "Téléchargez gratuitement les guides bien-être de Julie Zittel, thérapeute à Bayonne : réflexologie plantaire, apaiser les bouffées de chaleur, et bien plus à venir."],
  "/guide-bouffees-de-chaleur": ["Guide offert : apaiser les bouffées de chaleur par la réflexologie — Julie Zittel", "Recevez gratuitement le guide « 3 points de réflexologie pour apaiser les bouffées de chaleur » : auto-massage doux à faire chez soi. Par Julie Zittel, thérapeute à Bayonne."],
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    let entry = SEO_MAP[pathname];
    if (!entry && pathname.startsWith("/soins/")) {
      const soin = SOINS.find((s) => s.slug === pathname.split("/")[2]);
      if (soin) entry = [`${soin.name} (${soin.price}, ${soin.duration}) — Julie Zittel, Bayonne`, soin.intro];
    }
    if (entry) {
      document.title = entry[0];
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", entry[1]);
    }
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-beige-100 text-ink font-sans min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/soins" element={<Soins />} />
            <Route path="/soins/:slug" element={<SoinDetail />} />
            <Route path="/formations" element={<Formations />} />
            <Route path="/constellations" element={<Constellations />} />
            <Route path="/ateliers" element={<Ateliers />} />
            <Route path="/sejour-initiatique" element={<Sejour />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guide-bouffees-de-chaleur" element={<GuideBouffees />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/trek-sahara" element={<LandingTrek />} />
            <Route path="/formation-chi-nei-tsang" element={<LandingCNT />} />
            <Route path="/formation-reflexologie-plantaire" element={<LandingReflexologie />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
        <a
          href="https://wa.me/33623502131?text=Bonjour%20Julie%2C%20je%20viens%20de%20ton%20site%2C%20j%27aimerais%20un%20renseignement"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-button"
          aria-label="Contacter Julie sur WhatsApp"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_10px_25px_-8px_rgba(37,211,102,0.6)] transition-all duration-300 hover:scale-110 hover:shadow-[0_14px_30px_-8px_rgba(37,211,102,0.7)]"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
        <Toaster position="bottom-right" toastOptions={{ style: { background: "#313F15", color: "#F7F4EE", border: "1px solid #4A5D23" } }} />
      </BrowserRouter>
    </div>
  );
}

export default App;
